import { paths } from '@luxuryescapes/contract-customer-communication'

import { LUXURY_ESCAPES } from 'constants/brands'

type fetchLEUserSubscriptionsResponse = paths['/api/customer-communication/v1/subscriptions/users/{userId}']['get']['responses']['200']['content']['application/json']['result']
type updateLEUserSubscriptionsV2Response = paths['/api/customer-communication/v2/subscriptions/users']['patch']['responses']['200']['content']['application/json']
type updateLEUserSubscriptionsRequest = paths['/api/customer-communication/v1/subscriptions/users']['post']['parameters']['body']['payload']
type ServerSubscription = updateLEUserSubscriptionsRequest['subscriptions']
type updateLEUserSubscriptionsV2Request = paths['/api/customer-communication/v2/subscriptions/users']['patch']['parameters']['body']['payload']
type ServerSubscriptionV2 = updateLEUserSubscriptionsV2Request['subscriptions']

// Cadence format mappings between v2 API (uppercase) and form (lowercase)
type V2Cadence = App.SubscriptionPreferencesV2['todaysEscapesCadence']
type FormCadence = App.LESubscriptionsSettings['todaysEscapesCadence']

const v2ToFormCadenceMap: Record<V2Cadence, FormCadence> = {
  DEFAULT: 'daily',
  DAILY: 'daily',
  THREE_TIMES_WEEKLY: 'threeTimesWeekly',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
}

const formToV2CadenceMap: Record<FormCadence, V2Cadence> = {
  daily: 'DAILY',
  threeTimesWeekly: 'THREE_TIMES_WEEKLY',
  weekly: 'WEEKLY',
  monthly: 'MONTHLY',
}

/**
 * Convert v2 API cadence format (uppercase) to form-compatible format (lowercase)
 */
export function mapCadenceFromV2ToForm(cadence: V2Cadence): FormCadence {
  return v2ToFormCadenceMap[cadence] ?? 'daily'
}

/**
 * Convert form cadence format (lowercase) to v2 API format (uppercase)
 */
export function mapCadenceFromFormToV2(cadence: FormCadence): V2Cadence {
  return formToV2CadenceMap[cadence] ?? 'DAILY'
}

/**
 * Adapt v2 API settings to form-compatible format
 * Used in PreferenceCentrePage to normalize v2 settings for display
 */
export function adaptV2SettingsForForm(settings: App.SubscriptionPreferencesV2): App.LESubscriptionsSettings {
  return {
    emailSubscribed: settings.emailSubscribed,
    todaysEscapesSubscribed: settings.todaysEscapesSubscribed,
    myJourneySubscribed: settings.myJourneySubscribed,
    curatedCollectionSubscribed: settings.curatedCollectionSubscribed,
    appPushSubscribed: settings.appPushSubscribed,
    todaysEscapesCadence: mapCadenceFromV2ToForm(settings.todaysEscapesCadence),
    smsSubscribed: settings.smsSubscribed,
  }
}

/**
 * Convert form settings to v2 API format
 * Used when saving preferences for luxuryescapes brand
 */
export function mapFormSettingsToV2(settings: App.LESubscriptionsSettings): Partial<App.SubscriptionPreferencesV2> {
  return {
    emailSubscribed: settings.emailSubscribed,
    todaysEscapesSubscribed: settings.todaysEscapesSubscribed,
    myJourneySubscribed: settings.myJourneySubscribed,
    curatedCollectionSubscribed: settings.curatedCollectionSubscribed,
    appPushSubscribed: settings.appPushSubscribed,
    todaysEscapesCadence: mapCadenceFromFormToV2(settings.todaysEscapesCadence),
    smsSubscribed: settings.smsSubscribed,
  }
}

export function mapUserPreferences(res: fetchLEUserSubscriptionsResponse): App.LESubscriptionsSettings | App.WLSubscriptionsSettings {
  return res.brand === LUXURY_ESCAPES ? {
    emailSubscribed: res.email_subscribed,
    todaysEscapesSubscribed: res.todays_escapes_subscribed,
    myJourneySubscribed: res.my_journey_subscribed,
    curatedCollectionSubscribed: res.curated_collection_subscribed,
    appPushSubscribed: res.app_push_subscribed,
    todaysEscapesCadence: res.todays_escapes_cadence,
    smsSubscribed: res.sms_subscribed,

    // TODO when we enable whats app and web push
    // webPushSubscribed: res.web_push_subscribed,
    // whatsAppSubscribed: res.whats_app_subscribed,
  } : {
    smsSubscribed: res.sms_subscribed,
    appPushSubscribed: res.app_push_subscribed,
    emailSubscribed: res.email_subscribed,
    travelExpSubscribed: res.travel_exp_subscribed,
    travelExpCadence: res.travel_exp_cadence,
  }
}

export function mapUserPreferencesV2(subscriptions: updateLEUserSubscriptionsV2Response['result']['subscriptions']): App.LESubscriptionsV2Settings {
  return {
    settings: {
      emailSubscribed: subscriptions.email_subscribed,
      todaysEscapesSubscribed: subscriptions.todays_escapes_subscribed,
      myJourneySubscribed: subscriptions.my_journey_subscribed,
      curatedCollectionSubscribed: subscriptions.curated_collection_subscribed,
      appPushSubscribed: subscriptions.app_push_subscribed,
      todaysEscapesCadence: subscriptions.todays_escapes_cadence,
      smsSubscribed: subscriptions.sms_subscribed,

      // TODO when we enable whats app and web push
      // webPushSubscribed: subscriptions.web_push_subscribed,
      // whatsAppSubscribed: subscriptions.whats_app_subscribed,
    },
    emailSubscribedUpdatedAt: subscriptions.email_subscribed_updated_at,
  }
}

// Type guards
function isLESubscription(subscriptions: App.LESubscriptionsSettings | App.WLSubscriptionsSettings): subscriptions is App.LESubscriptionsSettings {
  return 'todaysEscapesSubscribed' in subscriptions || 'curatedCollectionSubscribed' in subscriptions || 'myJourneySubscribed' in subscriptions || 'todaysEscapesCadence' in subscriptions
}

function isWLSubscription(subscriptions: App.LESubscriptionsSettings | App.WLSubscriptionsSettings): subscriptions is App.WLSubscriptionsSettings {
  return 'travelExpSubscribed' in subscriptions || 'travelExpCadence' in subscriptions
}

export function mapSubscriptionToServer(subscriptions: App.LESubscriptionsSettings | App.WLSubscriptionsSettings): ServerSubscription {
  let subscriptionData: ServerSubscription = {
    email_subscribed: subscriptions.emailSubscribed,
    sms_subscribed: subscriptions.smsSubscribed,
    app_push_subscribed: subscriptions.appPushSubscribed,
  }
  if (isLESubscription(subscriptions)) {
    subscriptionData = {
      ...subscriptionData,
      todays_escapes_subscribed: subscriptions.todaysEscapesSubscribed,
      my_journey_subscribed: subscriptions.myJourneySubscribed,
      curated_collection_subscribed: subscriptions.curatedCollectionSubscribed,
      todays_escapes_cadence: subscriptions.todaysEscapesCadence,
    }
  }
  if (isWLSubscription(subscriptions)) {
    subscriptionData = {
      ...subscriptionData,
      travel_exp_subscribed: subscriptions.travelExpSubscribed,
      travel_exp_cadence: subscriptions.travelExpCadence,
    }
  }
  return subscriptionData
}

export function mapSubscriptionV2ToServer(subscriptions: Partial<App.SubscriptionPreferencesV2>): ServerSubscriptionV2 {
  return {
    email_subscribed: subscriptions.emailSubscribed,
    app_push_subscribed: subscriptions.appPushSubscribed,
    todays_escapes_subscribed: subscriptions.todaysEscapesSubscribed,
    curated_collection_subscribed: subscriptions.curatedCollectionSubscribed,
    my_journey_subscribed: subscriptions.myJourneySubscribed,
    todays_escapes_cadence: subscriptions.todaysEscapesCadence,
    sms_subscribed: subscriptions.smsSubscribed,
  }
}
